import { useTranslation } from "react-i18next";
import { calculateMonthDifference, calculateYearDifference } from "../utils/date";
import { fixNumbers } from "../utils/utility";
import { useEffect, useState } from "react";
import { flightDataMapper } from "../utils/flightDataMapper";

export const useFormValidations = passengers => {
    const { t } = useTranslation();
    const { isFlightInternational } = JSON.parse(sessionStorage.getItem("generalInformation")) || {};
    const selectedIntFlight = JSON.parse(sessionStorage.getItem("selectedIntFlight"));
    const selectedFlight = JSON.parse(sessionStorage.getItem("selectedFlight"));
    const domesticFlightInformation = JSON.parse(sessionStorage.getItem("domesticFlightInformation")) || {};
    const isTwoWay = domesticFlightInformation?.returnDate;

    //flight variables
    const normalizedDepartureFlight = flightDataMapper(isTwoWay, selectedFlight, selectedFlight?.segments);
    const normalizedReturnFlight = flightDataMapper(isTwoWay, selectedFlight, selectedFlight?.return_segments);

    const [flightDate, setFlightDate] = useState();
    useEffect(() => {
        let date;
        if (isFlightInternational) {
            date = !!selectedIntFlight?.return_segments?.length
                ? new Date(selectedIntFlight?.return_segments?.[selectedIntFlight?.return_segments?.length - 1]?.arrival_date)
                : new Date(selectedIntFlight?.segments[selectedIntFlight?.segments?.length - 1]?.arrival_date);
            setFlightDate(date);
        } else {
            date =
                selectedFlight?.return_segments?.lenght > 0
                    ? new Date(normalizedReturnFlight?.departureDate)
                    : new Date(normalizedDepartureFlight?.departureDate);
            setFlightDate(date);
        }
    }, []);

    return {
        first_name: {
            required: `${t("firstName")} ${t("isRequired")}`,
            minLength: { value: 2, message: `${t("firstNameValidation1")}` },
            pattern: { value: /^[\u0600-\u06FF\s]+$/, message: `${t("firstNameValidation2")}` },
        },
        last_name: {
            required: `${t("lastName")} ${t("isRequired")}`,
            minLength: { value: 2, message: `${t("lastNameValidation1")}` },
            pattern: { value: /^[\u0600-\u06FF\s]+$/, message: `${t("lastNameValidation2")}` },
        },
        english_first_name: {
            required: `${t("firstNameEn")} ${t("isRequired")}`,
            minLength: { value: 2, message: `${t("firstNameEnValidation1")}` },
            pattern: { value: /^[A-Za-z][A-Za-z0-9]*$/, message: `${t("firstNameENValidation2")}` },
        },
        english_last_name: {
            required: `${t("lastNameEn")} ${t("isRequired")}`,
            minLength: { value: 2, message: `${t("lastNameEnValidation1")}` },
            pattern: { value: /^[A-Za-z][A-Za-z0-9]*$/, message: `${t("lastNameEnValidation1")}` },
        },
        phone_number: {
            required: `${t("phoneNumber")} ${t("isRequired")}`,
            maxLength: { value: 11, message: `${t("phoneNumberValidation1")}` },
            minLength: { value: 11, message: `${t("phoneNumberValidation2")}` },
            pattern: {
                value: /^(09|\u06F0\u06F9)[0-9\u06F0-\u06F9]{9}$/,
                message: `${t("phoneNumber")} ${t("isWrong")}`,
            },
        },
        email: {
            pattern: {
                value: /^[a-zA-Z0-9_\u0600-\u06FF\u06F0-\u06F9.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/,
                message: `${t("email")} ${t("isWrong")}`,
            },
        },
        national_code: {
            required: `${t("nationalCode")} ${t("isRequired")}`,
            validate: {
                validateNumber: nationalId => {
                    nationalId = fixNumbers(nationalId);
                    // Check length is 10
                    if (nationalId.length < 8 || 10 < nationalId.length) {
                        return `${t("nationalCode")} ${t("isWrong")}`;
                    }
                    //Check if all of the numbers are the same
                    if (
                        nationalId == "0000000000" ||
                        nationalId == "1111111111" ||
                        nationalId == "2222222222" ||
                        nationalId == "3333333333" ||
                        nationalId == "4444444444" ||
                        nationalId == "5555555555" ||
                        nationalId == "6666666666" ||
                        nationalId == "7777777777" ||
                        nationalId == "8888888888" ||
                        nationalId == "9999999999"
                    ) {
                        return `${t("nationalCode")} ${t("isWrong")}`;
                    }

                    // STEP 00 : if nationalId.lenght==8 add two zero on the left
                    if (nationalId.length < 10) {
                        let zeroNeeded = 10 - nationalId.length;

                        let zeroString = "";
                        if (zeroNeeded == 2) {
                            zeroString = "00";
                        } else {
                            zeroString = "0";
                        }

                        nationalId = zeroString.concat(nationalId);
                    }

                    // STEP 1: Sum all numbers
                    let sum = 0;
                    for (let i = 0; i < 9; i++) {
                        sum += nationalId.charAt(i) * (10 - i);
                    }

                    // STEP 2: MOD ON 11
                    let mod = sum % 11;

                    // STEP 3: Check with 2
                    let finalValue;
                    if (mod >= 2) {
                        finalValue = 11 - mod;
                    } else {
                        finalValue = mod;
                    }

                    // STEP 4: Final Step check with control value
                    if (finalValue != nationalId.charAt(9)) {
                        return `${t("nationalCode")} ${t("isWrong")}`;
                    }

                    const foundNationalCode = passengers?.filter(item => item.national_code === nationalId);

                    if (foundNationalCode && foundNationalCode.length > 1) {
                        return `${t("nationalCodeValidation")}`;
                    }

                    return true;
                },
            },
        },
        passport_no: {
            required: `${t("passport")} ${t("isRequired")}`,
            pattern: { value: /^[A-Za-z]{1,2}[\u06F0-\u06F90-9]{6,9}$/, message: `${t("passport")} ${t("isWrong")}` },
        },
        passport_country: { required: `${t("passportCountry")} ${t("isRequired")}` },
        passport_exp_date: {
            required: `${t("passportExpire")} ${t("isRequired")}`,
            validate: {
                validateNumber: (expireDate, testContext) => {
                    const dateOfExpire = new Date(expireDate);
                    const nowDate = new Date();

                    const diffrenceInTime = calculateMonthDifference(flightDate, dateOfExpire);
                    const diffrenceInTimeNow = calculateMonthDifference(nowDate, dateOfExpire);

                    const modifiedItem = testContext.passengers.find(item => item.passport_exp_date === expireDate);

                    if (diffrenceInTime < 6) {
                        return `${t("expirePassportValidation")}`;
                    }
                    if (diffrenceInTime === 6 && dateOfExpire.getDate() <= flightDate.getDate()) {
                        return `${t("expirePassportValidation")}`;
                    }

                    if (modifiedItem?.passport_country?.id == 1) {
                        if (diffrenceInTimeNow > 60) {
                            return `${t("expirePassportValidation1")}`;
                        }
                        if (diffrenceInTimeNow === 60 && dateOfExpire.getDate() > nowDate.getDate()) {
                            return `${t("expirePassportValidation1")}`;
                        }
                    } else {
                        if (diffrenceInTimeNow > 120) {
                            return `${t("expirePassportValidation2")}`;
                        }
                        if (diffrenceInTimeNow === 120 && dateOfExpire.getDate() > nowDate.getDate()) {
                            return `${t("expirePassportValidation2")}`;
                        }
                    }
                    return true;
                },
            },
        },
        nationality: { required: `${t("nationality")} ${t("isRequired")}` },
        gender: { required: `${t("gender")} ${t("isRequired")}` },
        birthdate: {
            required: `${t("birthday")} ${t("isRequired")}`,
            validate: {
                validateNumber: (birthdate, testContext) => {
                    const dateOfBirth = new Date(birthdate);
                    const now = new Date();
                    const diffrenceInTime = calculateYearDifference(dateOfBirth, now);
                    const modifiedItem = testContext.passengers.find(item => item.birthdate === birthdate);

                    if ((modifiedItem?.passenger_type === "ADL" || modifiedItem?.type === "adt") && diffrenceInTime > 12) {
                        return true;
                    }
                    if ((modifiedItem?.passenger_type === "CHD" || modifiedItem?.type === "chd") && diffrenceInTime > 2 && diffrenceInTime <= 12) {
                        return true;
                    }
                    if ((modifiedItem?.passenger_type === "INF" || modifiedItem?.type === "inf") && diffrenceInTime <= 2 && diffrenceInTime >= 0) {
                        return true;
                    }
                    return `${t("birthday")} ${t("isWrong")}`;
                },
            },
        },
        card_number: {
            required: `${t("Form Messages.Card Number")} ${t("isRequired")}`,
            pattern: {
                value: /^[\d۰-۹0-9]{4}\s?[\d۰-۹0-9]{4}\s?[\d۰-۹0-9]{4}\s?[\d۰-۹0-9]{4}$/,
                message: t("Form Messages.Invalid Card Number Length"),
            },
            validate: {
                validateCardNumber: cardNumber => {
                    // Normalize and clean the card number
                    const normalizedNumber = fixNumbers(cardNumber);
                    const cleanNumber = normalizedNumber.replace(/\s/g, "");
                    const digits = cleanNumber.split("").map(Number);

                    // Apply Luhn algorithm
                    const luhnDigits = digits.map((digit, index) => {
                        const isOddPosition = (index + 1) % 2 === 0;
                        const multiplier = isOddPosition ? 1 : 2;
                        const multiplied = digit * multiplier;

                        // If multiplied value > 9, subtract 9 per Luhn algorithm
                        return multiplied > 9 ? multiplied - 9 : multiplied;
                    });

                    // Sum all digits and check if divisible by 10
                    const sum = luhnDigits.reduce((total, current) => total + current, 0);
                    const isValid = sum % 10 === 0;

                    return isValid ? true : `${t("Form Messages.Card Number")} ${t("isWrong")}`;
                },
            },
        },
        account_number: {
            required: `${t("Form Messages.Account Number")} ${t("isRequired")}`,
            pattern: { value: /^[۰-۹0-9]*$/, message: t("Form Messages.Invalid Account Number") },
        },
        sheba_number: {
            required: `${t("Form Messages.IBAN number")} ${t("isRequired")}`,
            pattern: { value: /^[۰-۹0-9]{2}[۰-۹0-9]{22}$/, message: t("Form Messages.Invalid IBAN Number") },
        },
        bank: {
            required: `${t("Form Messages.Bank name")} ${t("isRequired")}`,
        },
    };
};
